import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import CaseDashboard from "./components/CaseDashboard";
import AdminPanel from "./components/AdminPanel";
import ContactPage from "./components/ContactPage";
import SignInPage from "./components/SignInPage";
import RegisterPage from "./components/RegisterPage";
import UserDashboard from "./components/UserDashboard";
import PriceRequestPage from "./components/PriceRequestPage";
import PaymentSuccessView from "./components/PaymentSuccessView";
import PaymentCancelView from "./components/PaymentCancelView";
import { LogIn, Mail, TrendingUp, FileText, Calculator, Plus, CheckCircle, Sparkles, Zap, CalendarCheck, ArrowRight } from "lucide-react";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";
import { SessionExpiredDialog } from "./components/SessionExpiredDialog";
import { MobileNav } from "./components/MobileNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [currentView, setCurrentView] = useState("home"); // home, case, admin, contact, signin, register, dashboard, payment-success, payment-cancel
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [contactPlan, setContactPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");
  // When the Sign In page detects admin credentials it passes the pre-built
  // `Basic …` header here so the Admin Panel can skip its own login modal.
  const [adminAuthHeader, setAdminAuthHeader] = useState("");
  // When an admin clicks "Open Dashboard" on a user row we flip the main
  // view to that user's UserDashboard in read-only "impersonate" mode.
  const [impersonateUsername, setImpersonateUsername] = useState(null);

  const impersonateAs = (username) => {
    if (!username) return;
    setImpersonateUsername(username);
    setCurrentUser({ username, is_impersonated: true });
    setCurrentView("dashboard");
  };

  // Admin → directly open a specific case belonging to another user.
  // Powers the "click a case in the drill-down" shortcut in <AdminUsers/>:
  // enters impersonation mode AND lands on CaseDashboard in one step so
  // the admin doesn't have to go through the user's dashboard first.
  const openUserCaseAsAdmin = (username, caseId) => {
    if (!username || !caseId) return;
    setImpersonateUsername(username);
    setCurrentUser({ username, is_impersonated: true });
    setCurrentCaseId(caseId);
    setCurrentView("case");
  };

  const exitImpersonation = () => {
    setImpersonateUsername(null);
    setCurrentUser(null);
    setCurrentView("admin");
  };

  // On first load, inspect the URL for Stripe's post-checkout return paths
  // (`/payment/success?session_id=…` and `/payment/cancel`). The SPA is
  // state-routed so we flip currentView when we spot either path.
  React.useEffect(() => {
    const path = window.location.pathname || "";
    if (path.startsWith("/payment/success")) {
      setCurrentView("payment-success");
    } else if (path.startsWith("/payment/cancel")) {
      setCurrentView("payment-cancel");
    }
  }, []);

  // Stripe Checkout — called from the landing-page Pricing CTAs. Sends only
  // the lookup_key (amounts live server-side) and redirects to Stripe-hosted
  // Checkout. On return the useEffect above lands the user on /payment/success.
  const PRICING_LOOKUP_KEYS = {
    Standard: "pv_standard_monthly",
    Premium: "pv_premium_monthly",
    Professional: "pv_professional_monthly",
  };
  const startStripeCheckout = async (planName) => {
    const lookupKey = PRICING_LOOKUP_KEYS[planName];
    if (!lookupKey) {
      // No price mapped (e.g. "Contact Sales"-style plans) → use contact form.
      navigateToContact(planName);
      return;
    }
    try {
      setCheckoutError("");
      setLoading(true);
      const resp = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        user_id: currentUser?.username || null,
      });
      if (resp.data?.checkout_url) {
        window.location.href = resp.data.checkout_url;
        return;
      }
      setCheckoutError("Checkout session could not be created.");
    } catch (err) {
      setCheckoutError(err?.response?.data?.detail || err?.message || "Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  const createNewCase = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/cases`, {
        title: "New Valuation"
      });
      
      // Navigate to the new case
      setCurrentCaseId(response.data.id);
      setCurrentView("case");
    } catch (error) {
      console.error("Error creating new case:", error);
      alert("Failed to create new case. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const navigateToHome = () => {
    setCurrentView("home");
    setCurrentCaseId(null);
  };

  const navigateToAdmin = () => {
    setCurrentView("admin");
  };

  const navigateToContact = (plan) => {
    setContactPlan(plan || null);
    setCurrentView("contact");
  };

  const navigateToSignIn = () => {
    setCurrentView("signin");
  };

  const navigateToRegister = () => {
    setCurrentView("register");
  };

  const handleSignInSuccess = (userData) => {
    // Store the OTP-verified Blexbot session for every user, including
    // Admin1. Admin1 is detected via `is_admin: true` returned by the
    // verify-otp endpoint and routed to the Admin Panel; everyone else
    // lands on the User Dashboard.
    if (userData?.session_token) {
      try { sessionStorage.setItem('pv_session_token', userData.session_token); } catch { /* ignore */ }
    }
    if (userData?.is_admin) {
      setAdminAuthHeader(""); // legacy Basic header no longer used
      setIsAuthenticated(true);
      setCurrentUser(userData);
      setCurrentView("admin");
      return;
    }
    setIsAuthenticated(true);
    setCurrentUser(userData);
    setCurrentView("dashboard"); // Navigate to user dashboard
  };

  const handleRegisterSuccess = () => {
    // After registration the user must still verify via WhatsApp on sign-in.
    setCurrentView("signin");
  };

  const createNewCaseFromDashboard = async () => {
    await createNewCase();
  };

  const openExistingCase = (caseId) => {
    setCurrentCaseId(caseId);
    setCurrentView("case");
  };

  const [sessionExpired, setSessionExpired] = useState(false);
  const currentViewRef = React.useRef(currentView);
  currentViewRef.current = currentView;
  useEffect(() => {
    const onExpired = () => {
      if (["case", "dashboard", "price-requests"].includes(currentViewRef.current)) setSessionExpired(true);
    };
    window.addEventListener("pv:session-expired", onExpired);
    return () => window.removeEventListener("pv:session-expired", onExpired);
  }, []);

  const signInAgain = () => {
    setSessionExpired(false);
    setIsAuthenticated(false);
    setCurrentUser(null);
    setImpersonateUsername(null);
    try { sessionStorage.removeItem("pv_session_token"); } catch { /* ignore */ }
    try { sessionStorage.removeItem("pv_admin_auth"); } catch { /* ignore */ }
    setCurrentView("signin");
  };

  const expiredDialog = (
    <SessionExpiredDialog open={sessionExpired} onSignIn={signInAgain} />
  );

  // Render different views based on currentView state
  if (currentView === "dashboard" && (isAuthenticated || impersonateUsername)) {
    return (
      <div className="page-transition">
        <UserDashboard 
          user={currentUser}
          impersonateUsername={impersonateUsername}
          adminAuthHeader={adminAuthHeader}
          onExitImpersonation={exitImpersonation}
          onNavigateBack={() => {
            if (impersonateUsername) {
              exitImpersonation();
              return;
            }
            setIsAuthenticated(false);
            setCurrentUser(null);
            navigateToHome();
          }}
          onCreateNewCase={createNewCaseFromDashboard}
          onOpenCase={openExistingCase}
          onOpenPriceRequests={() => setCurrentView("price-requests")}
        />
        {expiredDialog}
      </div>
    );
  }

  if (currentView === "price-requests" && isAuthenticated) {
    return (
      <div className="page-transition">
        <PriceRequestPage
          user={currentUser}
          onNavigateBack={() => setCurrentView("dashboard")}
        />
        {expiredDialog}
      </div>
    );
  }

  if (currentView === "case" && currentCaseId) {
    return (
      <div className="page-transition">
        <CaseDashboard 
          caseId={currentCaseId}
          currentUser={currentUser}
          onNavigateBack={() => {
            if (isAuthenticated) {
              setCurrentView("dashboard");
            } else {
              navigateToHome();
            }
          }}
        />
        {expiredDialog}
      </div>
    );
  }

  if (currentView === "admin") {
    return (
      <div className="page-transition">
        <AdminPanel 
          onNavigateBack={() => {
            setAdminAuthHeader("");
            setIsAuthenticated(false);
            setCurrentUser(null);
            try { sessionStorage.removeItem("pv_admin_auth"); } catch { /* ignore */ }
            navigateToHome();
          }}
          onRequestReverify={() => {
            // Admin's Blexbot session expired — clear everything and send
            // them back to Sign In to re-verify via WhatsApp.
            setAdminAuthHeader("");
            setIsAuthenticated(false);
            setCurrentUser(null);
            try { sessionStorage.removeItem("pv_session_token"); } catch { /* ignore */ }
            try { sessionStorage.removeItem("pv_admin_auth"); } catch { /* ignore */ }
            setCurrentView("signin");
          }}
          onImpersonate={impersonateAs}
          onOpenUserCase={openUserCaseAsAdmin}
        />
      </div>
    );
  }

  if (currentView === "contact") {
    return (
      <ContactPage onNavigateBack={navigateToHome} initialPlan={contactPlan} />
    );
  }

  if (currentView === "signin") {
    return (
      <SignInPage 
        onNavigateBack={navigateToHome}
        onNavigateToRegister={navigateToRegister}
        onSignInSuccess={handleSignInSuccess}
      />
    );
  }

  if (currentView === "register") {
    return (
      <RegisterPage 
        onNavigateBack={navigateToHome}
        onNavigateToSignIn={navigateToSignIn}
        onRegisterSuccess={handleRegisterSuccess}
      />
    );
  }

  if (currentView === "payment-success") {
    return <PaymentSuccessView onNavigateHome={() => { window.history.replaceState({}, '', '/'); navigateToHome(); }} />;
  }

  if (currentView === "payment-cancel") {
    return <PaymentCancelView onNavigateHome={() => { window.history.replaceState({}, '', '/'); navigateToHome(); }} />;
  }

  // Home page - Welcome screen
  return (
    <>
    <Toaster position="bottom-right" closeButton={false} richColors />
    <div className="min-h-screen bg-white page-transition">
      {/* Header */}
      <div className="relative bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png" 
              alt="PriceValuator Logo" 
              className="h-28 sm:h-48 min-w-0 max-w-[70%] sm:max-w-none object-contain object-left"
            />
            <MobileNav onSignIn={navigateToSignIn} onContact={() => navigateToContact()} />
            <nav className="hidden sm:flex items-center gap-3">
              <Button
                onClick={navigateToSignIn}
                data-testid="header-signin-btn"
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-5 py-2 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 font-semibold"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
              <Button
                onClick={() => navigateToContact()}
                data-testid="header-contact-btn"
                variant="outline"
                className="border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50 rounded-full px-5 py-2 font-semibold"
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </Button>
            </nav>
          </div>
        </div>
      </div>

      {/* Welcome Content */}
      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-[#1E3A5F] mb-6">
            Welcome to <span className="text-[#1E3A5F]">PriceValuator</span>!
          </h2>
          <p className="text-xl text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed">
            We're thrilled to introduce PriceValuator, the cutting-edge AI-powered API that delivers fast and accurate fair market price calculations for your commercial content.
          </p>
          <p className="text-xl text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed">
            Leveraging advanced AI technology, a proven algorithm, and a validated database of over 1 million products, PriceValuator empowers you to match prices quickly and precisely to current market values.
          </p>
          <p className="text-xl text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed">
            With PriceValuator, you gain speed, reliability, and confidence in every appraisal decision.
          </p>
          <p className="text-xl font-bold text-[#14b8a6] mb-4 max-w-3xl mx-auto leading-relaxed">
            Start optimizing your appraisal process today!
          </p>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <p className="text-lg text-gray-600 mb-8">
            Experience a smarter, faster, and more efficient way to handle contents valuations—PriceValuator is here 
            to make your job easier and more precise.
          </p>
        </div>

        {/* Section Divider */}
        <div
          data-testid="section-divider-hero-discover"
          className="mt-16 flex items-center justify-center gap-3"
          aria-hidden="true"
        >
          <span className="h-px w-24 sm:w-40 bg-gradient-to-r from-transparent via-cyan-300 to-cyan-500"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 shadow-md shadow-cyan-400/40"></span>
          <span className="h-px w-24 sm:w-40 bg-gradient-to-l from-transparent via-cyan-300 to-cyan-500"></span>
        </div>

        {/* Discover / Features Section */}
        <section
          id="discover"
          data-testid="discover-section"
          className="mt-16"
        >
          {/* Top: main pitch + feature checklist (full-width) */}
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              AI-Powered Valuations
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4 leading-tight">
              Discover AI-Powered Equipment Valuations
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mb-8 max-w-3xl mx-auto">
              Unlock the power of Price Valuator, the intelligent platform designed to help appraisers,
              insurance professionals, equipment dealers, and businesses obtain accurate equipment values
              in minutes.
            </p>
            <ul
              data-testid="discover-features-list"
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-gray-700 text-left max-w-3xl mx-auto"
            >
              {[
                "AI-powered market research and valuation estimates",
                "Access to thousands of products in our valuation database",
                "Online market research and price comparisons",
                "Batch requests for multiple items",
                "Professional PDF valuation reports",
                "Custom research for products not found in the database",
                "Multi-user access for teams and enterprises",
                "Fast, accurate, and reliable results",
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Below: Why Choose + Book a Demo, side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 mt-14">
            <div
              data-testid="why-choose-card"
              className="bg-gradient-to-br from-[#1E3A5F] to-[#0f2540] text-white rounded-2xl p-8 shadow-xl"
            >
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-cyan-300" />
                <h3 className="text-xl font-bold text-white">Why Choose Price Valuator?</h3>
              </div>
              <ul className="space-y-2.5 text-sm text-cyan-50/90">
                {[
                  "Save hours of manual research",
                  "Improve valuation accuracy",
                  "Generate professional reports instantly",
                  "Access current market information from multiple sources",
                  "Increase productivity with batch processing and AI-assisted research",
                ].map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="book-demo-card"
              className="bg-white border-2 border-cyan-200 rounded-2xl p-8 shadow-lg flex flex-col"
            >
              <div className="flex items-center gap-2 mb-3">
                <CalendarCheck className="w-5 h-5 text-cyan-600" />
                <h3 className="text-xl font-bold text-[#1E3A5F]">Book a Demo</h3>
              </div>
              <p className="text-gray-600 text-sm mb-2">
                See how Price Valuator can transform your valuation process and help your team deliver
                faster, more accurate results.
              </p>
              <p className="text-gray-800 text-sm font-semibold mb-5">
                Book a Demo Today and Explore All the Features Price Valuator Has to Offer.
              </p>
              <div className="mt-auto">
                <Button
                  onClick={() => navigateToContact()}
                  data-testid="book-demo-cta"
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full font-semibold shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
                >
                  <CalendarCheck className="w-4 h-4 mr-2" />
                  Book a Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div
          data-testid="section-divider-discover-pricing"
          className="mt-20 flex items-center justify-center gap-3"
          aria-hidden="true"
        >
          <span className="h-px w-24 sm:w-40 bg-gradient-to-r from-transparent via-cyan-300 to-cyan-500"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 shadow-md shadow-cyan-400/40"></span>
          <span className="h-px w-24 sm:w-40 bg-gradient-to-l from-transparent via-cyan-300 to-cyan-500"></span>
        </div>

        {/* Pricing Section */}
        <section id="pricing" data-testid="pricing-section" className="mt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-3">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Pick the plan that matches your team's valuation volume. Cancel or upgrade anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Standard */}
            <div
              data-testid="pricing-card-standard"
              className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:border-cyan-300 transition-all duration-300 flex flex-col"
            >
              <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-cyan-600">
                Standard Plan
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#1E3A5F]">CAD $99</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="text-gray-600 text-sm mb-6">
                Ideal for independent appraisers and small businesses.
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-8 flex-1">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Access to Price Valuator database</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Online product research</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Up to 50 product searches per month</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Limited batch requests (up to 20 items per request)</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Standard support</li>
              </ul>
              <Button
                onClick={() => navigateToContact("Standard")}
                data-testid="pricing-cta-standard"
                variant="outline"
                className="w-full border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50 rounded-full font-semibold"
              >
                Get Started
              </Button>
            </div>

            {/* Premium (highlighted) */}
            <div
              data-testid="pricing-card-premium"
              className="relative bg-gradient-to-br from-cyan-50 to-white border-2 border-cyan-500 rounded-2xl p-8 shadow-xl transition-all duration-300 flex flex-col md:-translate-y-2"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                MOST POPULAR
              </div>
              <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-cyan-700">
                Premium Plan
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#1E3A5F]">CAD $199</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="text-gray-600 text-sm mb-6">
                Designed for growing appraisal and insurance teams.
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-8 flex-1">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Full access to Price Valuator database</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Online product research</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Up to 150 product searches per month</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Batch requests up to 50 items per request</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Professional PDF valuation reports</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Priority support</li>
              </ul>
              <Button
                onClick={() => navigateToContact("Premium")}
                data-testid="pricing-cta-premium"
                className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full font-semibold shadow-lg"
              >
                Get Started
              </Button>
            </div>

            {/* Professional */}
            <div
              data-testid="pricing-card-professional"
              className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:border-cyan-300 transition-all duration-300 flex flex-col"
            >
              <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-cyan-600">
                Professional Plan
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#1E3A5F]">CAD $399</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="text-gray-600 text-sm mb-6">
                The complete solution for appraisal firms, insurers, and enterprise clients.
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-8 flex-1">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Unlimited database access</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Up to 1000 product research per month</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Unlimited batch requests</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Customized PDF valuation reports</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Priority and personalized support</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> Dedicated account assistance</li>
              </ul>
              <Button
                onClick={() => navigateToContact("Professional")}
                data-testid="pricing-cta-professional"
                variant="outline"
                className="w-full border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50 rounded-full font-semibold"
              >
                Get Started
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-gray-600 text-sm">
            ClaimValuator® - AI-Powered Appraisal | Calculate claim values with up-to-date market prices from our 1M+ product database.
          </p>
          <button
            type="button"
            onClick={navigateToAdmin}
            data-testid="footer-admin-link"
            className="text-xs text-gray-400 hover:text-cyan-700 underline"
          >
            Admin
          </button>
        </div>
      </footer>
    </div>
    </>
  );
}

export default App;